import { Conditional } from 'dg-web-shared/lib/ReactHelpers.tsx';
import { Clickable } from 'dg-web-shared/ui/Clickable.tsx';
import * as Icons16 from 'dg-web-shared/ui/icons/Icons16.tsx';
import { Icon16 } from '../icons/Icon.tsx';
import { Option as OptionBase } from './Option.ts';
import {
    HalfSlideIn,
    SecondLevelHeader,
    SlideInBody,
    SlideInHeaderTexts,
    SlideInInfoBox,
} from './Slidein.tsx';
import { SubheaderContainer } from './SlideInSubheader.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { css } from '@emotion/css';
import { OperatorTypo } from '../OperatorTypo.ts';
import { ColorHex } from '../Colors.ts';
import React, { type JSX } from 'react';
import { Typo } from 'dg-web-shared/ui/typo.ts';

export interface Option<I> extends OptionBase<I> {
    hasSpacingBottom?: boolean;
}

export interface MultiSelectionSlideInProps<I> {
    open: boolean;
    caption?: string;
    heading: React.ReactNode;
    selection: I[];
    options: Option<I>[];
    onLeftActionClick: () => void;
    setNewSelection: (selection: I[]) => void;
    outsideBody?: boolean;
    infoText?: JSX.Element | null;
}

function GenericMultiSelectionSlideIn<I>({
    open,
    caption,
    heading,
    selection,
    options,
    onLeftActionClick,
    setNewSelection,
    outsideBody,
    infoText,
}: MultiSelectionSlideInProps<I>) {
    function toggleItem(id: I): void {
        if (selection.indexOf(id) > -1) {
            setNewSelection(selection.filter(i => i !== id));
        } else {
            const copy = selection.slice();
            copy.push(id);
            setNewSelection(copy);
        }
    }

    function showAll(): boolean {
        return selection.length !== options.length;
    }

    function toggleAllNone(): void {
        if (showAll()) {
            setNewSelection(options.filter(o => !o.disabled).map(o => o.id));
        } else {
            setNewSelection(
                options
                    .filter(o => o.disabled && selection.includes(o.id))
                    .map(o => o.id),
            );
        }
    }

    function renderElements(): JSX.Element[] {
        return options.map((o: Option<I>) => {
            const selected = selection.indexOf(o.id) > -1;
            return (
                <Clickable
                    key={`clickable-${JSON.stringify(o.id)}`}
                    element="div"
                    disabled={o.disabled}
                    className={css([
                        {
                            ...OperatorTypo.headingOne,
                            fontWeight: 500,
                            lineHeight: '1.5em',
                            color: ColorHex.rgba(
                                ColorHex.darkblue,
                                o.disabled ? 0.4 : 0.8,
                            ),
                            background: ColorHex.rgba(ColorHex.darkblue, 0.2),
                            padding: '4px 48px',
                            margin: '0 -48px',
                            '&:hover': o.disabled
                                ? undefined
                                : {
                                      background: ColorHex.darkblue,
                                      color: ColorHex.white,
                                  },
                        },
                        !selected && {
                            background: 'none',
                            '&:hover': o.disabled
                                ? undefined
                                : {
                                      background: ColorHex.darkblue,
                                      color: ColorHex.white,
                                  },
                        },
                        o.hasSpacingBottom && {
                            marginBottom: '25px',
                        },
                    ])}
                    onClick={() => !o.disabled && toggleItem(o.id)}
                >
                    <div
                        className={css({
                            position: 'relative',
                            top: '3px',
                            marginRight: '16px',
                            display: 'inline-block',
                            verticalAlign: 'baseline',
                        })}
                    >
                        <Icon16
                            icon={
                                selected
                                    ? Icons16.checkbox.on
                                    : Icons16.checkbox.off
                            }
                        />
                    </div>
                    <span>{o.displayText}</span>
                </Clickable>
            );
        });
    }

    return (
        <HalfSlideIn open={open} outsideBody={outsideBody}>
            <SecondLevelHeader onClose={onLeftActionClick}>
                <SlideInHeaderTexts
                    subtitle={caption}
                    title={heading}
                    hasLeftIcon={false}
                />
            </SecondLevelHeader>
            <SubheaderContainer>
                <Clickable
                    element="div"
                    className={css({
                        ...Typo.captionC1,
                        color: ColorHex.rgba(ColorHex.darkblue, 0.7),
                        paddingTop: '2px',
                    })}
                    onClick={() => toggleAllNone()}
                >
                    <div
                        className={css({
                            position: 'relative',
                            top: '4px',
                            marginRight: '16px',
                            display: 'inline-block',
                            verticalAlign: 'baseline',
                        })}
                    >
                        <Icon16
                            icon={
                                showAll()
                                    ? Icons16.checkbox.all
                                    : Icons16.checkbox.none
                            }
                        />
                    </div>
                    <span>
                        {showAll() ? (
                            <Localized
                                de="alle"
                                fr="tous"
                                it="tutti"
                                en="all"
                            />
                        ) : (
                            <Localized
                                de="keine"
                                fr="aucunes"
                                it="nessuno"
                                en="none"
                            />
                        )}
                    </span>
                    <Conditional c={showAll()}>
                        <span className={css({ ...Typo.captionC2 })}>
                            {' ' + options.length}
                        </span>
                    </Conditional>
                </Clickable>
            </SubheaderContainer>
            <SlideInBody hasSubheader>
                {infoText && <SlideInInfoBox>{infoText}</SlideInInfoBox>}
                {renderElements()}
            </SlideInBody>
        </HalfSlideIn>
    );
}

// generic components crap, see https://github.com/Microsoft/TypeScript/issues/3960

export const NumberMultiSelectionSlideIn: (
    p: MultiSelectionSlideInProps<number>,
) => JSX.Element = GenericMultiSelectionSlideIn;

export const StringMultiSelectionSlideIn: (
    p: MultiSelectionSlideInProps<string>,
) => JSX.Element = GenericMultiSelectionSlideIn;
